import { Box, Flex, Icon, IconButton, Text, useToast } from '@chakra-ui/react';
import { FaEye } from '@react-icons/all-files/fa/FaEye';
import { FaEyeSlash } from '@react-icons/all-files/fa/FaEyeSlash';
import { Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import { decodeToken } from 'react-jwt';
import * as Yup from 'yup';

import { SUCCESS_TOASTS } from '../constants/toasts';
import { CookieKeys, useCookie } from '../hooks/useCookie';
import { StrongPasswordTooltipExplanation } from '../pages/auth/utils';
import { signupAndAuthorizeAsync } from '../services/auth0';
import { JWTToken, getEmailFromJWT } from '../utils/auth';
import { createSuccessToast } from '../utils/toast';
import { passwordSubmitErrorMessage, passwordValidation } from '../utils/validation/auth';
import PasswordStrength from './PasswordStrength';
import TextWithTooltip from './TextWithTooltip';
import Input from './formComponents/Input';
import PrimaryButton from './ui/buttons/PrimaryButton';

type Props = {
  onSuccess: () => void;
};

const validationSchema = Yup.object({
  new_password: passwordValidation,
});

interface Values {
  email: string;
  new_password: string;
}

const SavePasswordForm = ({ onSuccess }: Props) => {
  const [showPassword, setShowPassword] = useState(false);
  const [accessToken] = useCookie<string>(CookieKeys.ACCESS_TOKEN);
  const email = getEmailFromJWT(decodeToken<JWTToken>(accessToken ?? ''));
  const toast = useToast();

  const handleSubmit = async (values: Values, formikHelpers: FormikHelpers<Values>) => {
    try {
      if (!email) {
        throw new Error('Something went wrong, please try again.');
      }
      await signupAndAuthorizeAsync({ email, password: values.new_password });
      toast(createSuccessToast(SUCCESS_TOASTS.passwordSaved));
      onSuccess();
    } catch (e) {
      formikHelpers.setErrors({ new_password: passwordSubmitErrorMessage(e as Error) });
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  return (
    <Formik
      validateOnChange={false}
      validateOnBlur={false}
      initialValues={{ email: email ?? '', new_password: '' }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ values, isSubmitting }) => (
        <Form>
          <Flex flexDir="column" gap={4}>
            <Input
              type="email"
              label="Email"
              name="email"
              readOnly
              _input={{ autoComplete: 'username', bgColor: 'gray.50' }}
            />
            <Input
              type={showPassword ? 'text' : 'password'}
              label="Password"
              name="new_password"
              placeholder="Password"
              _input={{ autoComplete: 'new-password' }}
              icon={
                <IconButton
                  variant="unstyled"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label="Hide/Show password"
                  icon={<Icon as={showPassword ? FaEye : FaEyeSlash} mt={1} />}
                />
              }
            />
            <Box>
              <TextWithTooltip
                label={StrongPasswordTooltipExplanation}
                textProps={{ fontSize: '12px' }}
              >
                Password requirements
              </TextWithTooltip>
            </Box>
            {values.new_password && (
              <PasswordStrength h="auto" w="100%" password={values.new_password} />
            )}
            <PrimaryButton type="submit" w="100%" loading={isSubmitting}>
              SAVE PASSWORD
            </PrimaryButton>
            <Text fontSize="sm" color="taupeGray" textAlign="center">
              Your browser will offer to remember it for you.
            </Text>
          </Flex>
        </Form>
      )}
    </Formik>
  );
};

export default SavePasswordForm;
